import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Feedback } from '~/components/Feedback';
import { useEvents } from '~/hooks';
import { useAcquisitionPostForms } from '~/hooks/useAcquisitionPostForms';
import { useFormData } from '~/hooks/useFormData';
import { useSteps } from '~/hooks/useSteps';
import { configureEvents } from '~/utils/events';
import { getLogger } from '~/utils/logger';
import { generateSchema, generateDefaultValues, toStep, executeBehavior } from './utils';
import type { JSX } from 'react';
import type { SubmitHandler, SubmitErrorHandler } from 'react-hook-form';
import type { z } from 'zod';
import type { FormSchema } from '~/shared/domain/models';

enum ViewStatus {
  form = 'FORM',
  feedback = 'FEEDBACK',
}

export type FormBuilderProps = {
  data: FormSchema;
};

type Component = (props: FormBuilderProps) => JSX.Element;

export const FormBuilder: Component = ({ data }) => {
  const { formData, updateFormData, handleFormError } = useFormData();
  const { currentStep, hasNext, setTotalSteps, handleNext } = useSteps();
  const { events, setEvents } = useEvents();
  const defaultValues = useMemo(
    () => generateDefaultValues(data.components, formData),
    [data.components, formData],
  );
  const [status, setStatus] = useState<ViewStatus>(ViewStatus.form);
  const mutation = useAcquisitionPostForms({
    onSuccess: () => {
      executeBehavior(data.behavior);
      setStatus(ViewStatus.feedback);
    },
    onError: () => {
      handleFormError();
    },
  });

  useEffect(() => {
    if (data?.product) {
      setEvents(configureEvents(data.product));
    }
  }, [setEvents, data]);

  useEffect(() => {
    setTotalSteps(data.components.length);
  }, [data.components]);

  const validationSchema = useMemo(
    () => generateSchema(data.components[currentStep - 1]),
    [currentStep],
  );

  useEffect(() => {
    if (events.dispatchEventContentDisplay) {
      events.dispatchEventContentDisplay({
        element_name: 'form_builder',
        element_step: `step_${currentStep}`,
        element_url: window.location.href,
      });
    }
  }, [events, currentStep]);

  type ValidationSchema = z.infer<typeof validationSchema>;

  const methods = useForm<ValidationSchema>({
    resolver: async (values, context, options) => {
      return zodResolver(validationSchema)(values, context, options);
    },
    values: defaultValues,
    shouldUnregister: true,
  });

  const handleSendData = useCallback(
    async (values: any) => {
      if (hasNext) {
        return;
      }

      mutation.mutate({
        product: data.product,
        type: data.type,
        attributes: { ...formData, ...values },
      });
    },
    [formData, hasNext],
  );

  const submitHandler: SubmitHandler<ValidationSchema> = (values) => {
    updateFormData(values);
    handleNext();

    events.dispatchEventButtonClicked('continue', {
      element_step: `step_${currentStep}`,
      element_function: 'success',
    });

    if (!hasNext) {
      events.dispatchEventButtonIdentify(values);
    }

    handleSendData(values);
  };

  const submitErrorHandler: SubmitErrorHandler<ValidationSchema> = (errors) => {
    getLogger().warn(`[FormBuilder] - validation`, errors);
  };

  return (
    <FormProvider {...methods}>
      {status === ViewStatus.form && (
        <form onSubmit={methods.handleSubmit(submitHandler, submitErrorHandler)}>
          {data.components.map(toStep)}
        </form>
      )}
      {status === ViewStatus.feedback && <Feedback data={data.feedback} />}
    </FormProvider>
  );
};
